import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../authentication/AuthContext";
import {
  TagInput, IconUpload, InfoTooltip, RefSelect, SortableList, TAG_SUGGESTIONS, isValidSlug, SLUG_HINT,
  SCORE_TYPES, SCORE_TYPE_LABELS, SCORE_TYPE_DESC,
} from "./BuilderUtils";
import PageComposer from "./PageComposer";
import PageTypePicker from "./PageTypePicker";
import MeasurePoolSection from "./MeasurePoolSection";
import NotebookPanel from "./NotebookPanel";
import { scoreCatalog, matchesScoreSearch, copyScore } from "./scoreCatalog";
import { computeTerritoryLayers, territoryUsers, pageFitsConfig } from "./territoryConfig";
import { TERRITORIES_URL } from "../terrain3d/terrainBase";
import "../../builder.css";

const FIELD_TOOLTIPS = {
  _id: "Identifiant unique dans la base (slug). Utilisé dans le code et les URLs. Ne peut pas être modifié après création.",
  name: "Nom affiché aux animateurs dans le sélecteur d'atelier.",
  decision_metric_id: "ID du score utilisé comme budget/monnaie pour les décisions (ex: money, vote). Défini dans le score_dic.",
  custom_init: "Mode d'initialisation spécial. 'compte-gouttes' active le mode vote ACG avec balancing des scores par acteur.",
  nb_rounds_cg: "Nombre de rounds dans le mode Compte-Gouttes. Nécessite custom_init = 'compte-gouttes'.",
  target_per_round: "Points d'économies d'eau visés PAR TOUR, à terme : après le tour n la courbe d'objectif de la projection d'eau vise n × cette valeur, et le bilan de fin compare le résultat à (nombre de tours × cette valeur). Vide = 20.",
  tags: "Étiquettes pour catégoriser l'atelier (ex: 'btp', 'acg', 'disney', 'ville_paris') et filtrer les configs disponibles par animateur. Ce ne sont PAS des versions distinctes : deux ateliers avec le même tag restent des ateliers indépendants (piles, scores, décisions propres à chacun). Pour créer une variante d'un atelier existant, utilisez le bouton « Dupliquer ».",
  pile: "Séquence ordonnée des pages. L'ordre ici est l'ordre d'affichage dans le jeu. Certaines décisions peuvent insérer des pages dynamiquement. LA DERNIÈRE PAGE DE LA PILE EST L'ÉCRAN DE FIN : elle reste affichée quand la partie est terminée, sans son bouton, sous la barre de retour à l'accueil. Placez-y le bilan de l'atelier.",
  score_dic: "Dictionnaire de tous les scores initiaux du jeu. Chaque score a un id unique, un type, une valeur de départ et des bornes min/max.",
};

const emptyConfig = {
  _id: "",
  name: "",
  pile: [],
  score_dic: {},
  tags: [],
  decision_metric_id: "money",
  custom_init: "",
  nb_rounds_cg: "",
  target_per_round: "",
};

const emptyScore = {
  name: "",
  type: "metric",
  icon: "",
  value: 0,
  min: 0,
  max: "",
  description: "",
  coef: "",
  detail: "",
  standard_value: "",
  unit: "",
  metadata: null, // { standard: {sous_categorie: valeur}, current: {...} } si score composite
};


export default function ConfigEditor() {
  const { configId } = useParams();
  const location = useLocation();
  const isNew = configId === "new";
  const navigate = useNavigate();
  const { token } = useAuth();

  const [config, setConfig] = useState(emptyConfig);
  const [allPages, setAllPages] = useState([]);
  // Les ateliers complets, et non leurs seuls identifiants : leurs `score_dic`
  // alimentent « reprendre un score d'un autre atelier ».
  const [allConfigs, setAllConfigs] = useState([]);
  const allConfigIds = useMemo(() => allConfigs.map((c) => c._id), [allConfigs]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [pageSearch, setPageSearch] = useState("");
  const [showPagePicker, setShowPagePicker] = useState(false);
  // Deuxième temps de « Ajouter une page » : la grille des types, quand on a
  // choisi d'en créer une plutôt que d'en reprendre une.
  const [showPageTypePicker, setShowPageTypePicker] = useState(false);
  // Territoires disponibles : artefact statique produit par le pipeline
  // (api/experiments/terrain3d/build_manifest.py), servi depuis l'Object Storage
  // (cf. terrain3d/terrainBase.js) — pas un appel à l'API, donc pas d'authentification.
  const [territories, setTerritories] = useState([]);
  // Territoire tel qu'il est EN BASE, distinct de celui du formulaire :
  // c'est lui qui décide du verrouillage, pas la valeur en cours d'édition.
  const [savedTerritoryId, setSavedTerritoryId] = useState(null);

  // Page ouverte dans le composeur, par-dessus l'atelier (ou null). Un seul état
  // là où il y avait un aperçu ET un tiroir d'édition : voir une page et la
  // modifier sont le même geste, dans le même écran.
  const [composerPageId, setComposerPageId] = useState(null);
  // Type de la page à créer, quand le composeur est ouvert sur « new ».
  const [composerNewType, setComposerNewType] = useState(null);
  // Position visée dans la pile pour la page en cours de CRÉATION, ou null si
  // le composeur n'a pas été ouvert pour y ajouter une page. Sans ça, « Ajouter
  // une page → créer » écrivait bien la page en base mais ne la posait jamais
  // dans l'atelier : on la retrouvait ensuite dans la bibliothèque, en se
  // demandant pourquoi la pile n'avait pas bougé.
  const [creatingAt, setCreatingAt] = useState(null);

  // Résultat de la dernière vérification (GET /validate_game_config/:id), ou null si jamais lancée
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  // Panneau des carnets de rôle ouvert ou non. Il porte son propre chargement :
  // il lit l'atelier ENREGISTRÉ, comme la vérification, et n'a rien à attendre
  // du formulaire en cours d'édition.
  const [showNotebooks, setShowNotebooks] = useState(false);

  const openPageComposer = (pageId) => {
    setComposerNewType(null);
    setCreatingAt(null);
    setComposerPageId(pageId);
  };
  const closePageComposer = () => {
    setComposerPageId(null);
    setComposerNewType(null);
    setCreatingAt(null);
    // Recharge la liste : une page créée depuis le composeur doit apparaître
    // dans le sélecteur de pile, et un titre modifié dans la pile elle-même.
    apiGet("/get_all_pages").then(setAllPages);
  };

  // Créer une page depuis la pile : on connaît son type et la place qu'elle y
  // prendra, le composeur s'ouvre directement dessus.
  const createPageInPile = (type) => {
    setShowPageTypePicker(false);
    setPageSearch("");
    setComposerNewType(type);
    setCreatingAt(insertAt === null ? config.pile.length : insertAt);
    setInsertAt(null);
    setComposerPageId("new");
  };

  // Score editor state
  const [scoreKey, setScoreKey] = useState("");
  const [scoreData, setScoreData] = useState(emptyScore);
  const [editingScoreKey, setEditingScoreKey] = useState(null);
  const [showScoreForm, setShowScoreForm] = useState(false);
  const [scoreTypeTab, setScoreTypeTab] = useState("metric");
  // « + Ajouter » d'un groupe : le type visé (ou null), et la recherche dans
  // les scores des autres ateliers.
  const [scorePickerType, setScorePickerType] = useState(null);
  const [scorePickerSearch, setScorePickerSearch] = useState("");

  const apiGet = async (path) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
      headers: { Authorization: "Bearer " + token },
    });
    return res.json();
  };

  const apiPost = async (path, body) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
      method: "POST",
      headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return res.json();
  };

  const apiDelete = async (path) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
      method: "DELETE",
      headers: { Authorization: "Bearer " + token },
    });
    return res.json();
  };

  useEffect(() => {
    fetch(TERRITORIES_URL)
      .then((r) => (r.ok ? r.json() : { territories: [] }))
      .then((data) => setTerritories(data.territories || []))
      .catch(() => setTerritories([]));
  }, []);

  useEffect(() => {
    apiGet("/get_all_pages").then(setAllPages);
    apiGet("/get_all_game_configs").then((all) => setAllConfigs(Array.isArray(all) ? all : []));
    if (isNew && location.state?.duplicateFrom) {
      const data = location.state.duplicateFrom;
      setConfig({
        ...emptyConfig,
        ...data,
        _id: "",
        name: data.name ? `${data.name} (copie)` : "",
        custom_init: data.custom_init || "",
        nb_rounds_cg: data.nb_rounds_cg || "",
        target_per_round: data.target_per_round || "",
      });
      setLoading(false);
      setDirty(false);
    } else if (!isNew) {
      apiGet(`/get_game_config/${configId}`)
        .then((data) => {
          setConfig({
            ...emptyConfig,
            ...data,
            custom_init: data.custom_init || "",
            nb_rounds_cg: data.nb_rounds_cg || "",
            target_per_round: data.target_per_round || "",
          });
          setSavedTerritoryId(data.territory?.id || null);
          setDirty(false);
        })
        .finally(() => setLoading(false));
    }
  }, [configId]);

  // Avertit avant de fermer/rafraîchir l'onglet s'il y a des modifications non sauvegardées.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);

  const confirmDiscardIfDirty = () => {
    if (!dirty) return true;
    return window.confirm("Vous avez des modifications non sauvegardées. Voulez-vous vraiment quitter sans sauvegarder ?");
  };

  const handleBack = () => {
    if (!confirmDiscardIfDirty()) return;
    navigate("/builder");
  };

  const handleDuplicate = () => {
    navigate("/builder/config/new", { state: { duplicateFrom: config } });
  };

  const set = (field, value) => {
    setConfig((c) => ({ ...c, [field]: value }));
    setDirty(true);
  };

  // ── Pile management ──
  // --- Territoire de l'atelier -------------------------------------------
  const pagesById = useMemo(
    () => Object.fromEntries(allPages.map((p) => [p._id, p])),
    [allPages]
  );
  const territoryId = config.territory?.id || "";
  // Figé dès qu'un atelier ENREGISTRÉ en porte un. Un atelier en cours de
  // création, ou qui n'en a jamais eu, reste libre de s'en donner un.
  const territoryLocked = !isNew && Boolean(savedTerritoryId);
  const territoryLayers = useMemo(
    () => (territoryId ? computeTerritoryLayers(config.pile, pagesById) : []),
    [territoryId, config.pile, pagesById]
  );
  const territoryPages = useMemo(
    () => (territoryId ? territoryUsers(config.pile, pagesById) : []),
    [territoryId, config.pile, pagesById]
  );
  const territoryCatalog = territories.find((t) => t.id === territoryId) || null;
  // Une couche demandée par une page mais absente du territoire choisi ne
  // produirait aucune erreur en jeu — elle serait juste invisible. C'est
  // précisément le genre de silence qu'un éditeur doit rompre.
  const unknownLayers = territoryCatalog
    ? territoryLayers.filter((id) => !(territoryCatalog.layers || []).includes(id))
    : [];

  // Chaque entrée de la pile est identifiée par `${pageId}::${occurrence}` pour
  // permettre le drag & drop même quand une même page apparaît plusieurs fois.
  const pileItems = config.pile.map((pageId, idx) => {
    const occurrence = config.pile.slice(0, idx).filter((id) => id === pageId).length;
    return { id: `${pageId}::${occurrence}`, pageId };
  });

  const [insertAt, setInsertAt] = useState(null); // position d'insertion choisie avant d'ouvrir le picker

  const movePage = (idx, dir) => {
    const pile = [...config.pile];
    const target = idx + dir;
    if (target < 0 || target >= pile.length) return;
    [pile[idx], pile[target]] = [pile[target], pile[idx]];
    set("pile", pile);
  };

  const removePage = (idx) => {
    const pile = [...config.pile];
    pile.splice(idx, 1);
    set("pile", pile);
  };

  const reorderPile = (newItems) => {
    set("pile", newItems.map((i) => i.pageId));
  };

  // Duplique la page `pageId` (à la position `idx` de la pile) : crée une copie
  // indépendante et remplace cette entrée par la copie, pour que seul CET atelier
  // soit affecté — les autres ateliers/pages continuent de référencer l'originale.
  const handleDuplicatePageInPile = async (idx, pageId) => {
    const suggested = `${pageId}_copie`;
    const newId = window.prompt(
      `Identifiant de la copie de la page « ${pageId} » :`,
      allPages.some((p) => p._id === suggested) ? "" : suggested
    );
    if (!newId) return;
    if (!isValidSlug(newId)) return alert(`Identifiant invalide.\n${SLUG_HINT}`);
    if (allPages.some((p) => p._id === newId)) return alert(`L'identifiant "${newId}" est déjà utilisé par une autre page.`);

    const res = await apiPost("/duplicate_page", { source_id: pageId, new_id: newId });
    if (!res.success) return alert("Erreur : " + res.message);

    const pile = [...config.pile];
    pile[idx] = newId;
    set("pile", pile);
    setAllPages((prev) => [...prev, res.page]);
    alert(`Page dupliquée en « ${newId} » et reconnectée dans la pile de cet atelier. N'oubliez pas de sauvegarder l'atelier.`);
  };

  const addPageToPile = (pageId) => {
    // Le sélecteur désactive déjà les pages incompatibles ; ce garde-fou couvre
    // les autres chemins d'ajout (duplication, création depuis le tiroir).
    const fit = pageFitsConfig(allPages.find((p) => p._id === pageId), config.territory?.id);
    if (!fit.ok) return alert(`Impossible d'ajouter cette page.\n\n${fit.reason}\n\nDupliquez-la pour en faire une version sur ce territoire.`);
    const pile = [...config.pile];
    if (insertAt === null || insertAt >= pile.length) pile.push(pageId);
    else pile.splice(insertAt, 0, pageId);
    set("pile", pile);
    setShowPagePicker(false);
    setPageSearch("");
    setInsertAt(null);
  };

  const openPagePickerAt = (position) => {
    setInsertAt(position);
    setShowPagePicker(true);
  };

  // ── Score dict ──
  const scoresByType = SCORE_TYPES.reduce((acc, t) => {
    acc[t] = Object.entries(config.score_dic).filter(([, s]) => s.type === t);
    return acc;
  }, {});

  const openNewScore = (type = "metric") => {
    setEditingScoreKey(null);
    setScoreKey("");
    setScoreData({ ...emptyScore, type });
    setShowScoreForm(true);
    setScoreTypeTab(type);
  };

  const openEditScore = (key) => {
    setEditingScoreKey(key);
    setScoreKey(key);
    setScoreData({ ...emptyScore, ...config.score_dic[key], metadata: config.score_dic[key].metadata || null });
    setShowScoreForm(true);
  };

  // ── Score composite : sous-catégories (metadata.standard / metadata.current) ──
  const toggleComposite = (enabled) => {
    setScoreData((s) => ({ ...s, metadata: enabled ? { standard: {}, current: {} } : null }));
  };

  const addSubcategory = () => {
    setScoreData((s) => {
      let name = "sous_categorie";
      let i = 2;
      while (name in (s.metadata?.standard || {})) { name = `sous_categorie_${i}`; i += 1; }
      const standard = { ...(s.metadata?.standard || {}), [name]: 0 };
      const current = { ...(s.metadata?.current || {}), [name]: 0 };
      return recomputeCompositeValue({ ...s, metadata: { standard, current } });
    });
  };

  const renameSubcategory = (oldName, newName) => {
    if (!newName || newName === oldName) return;
    setScoreData((s) => {
      const standard = { ...(s.metadata?.standard || {}) };
      const current = { ...(s.metadata?.current || {}) };
      if (newName in standard) return s; // évite d'écraser une autre sous-catégorie
      standard[newName] = standard[oldName];
      current[newName] = current[oldName];
      delete standard[oldName];
      delete current[oldName];
      return { ...s, metadata: { standard, current } };
    });
  };

  const updateSubcategoryValue = (name, value) => {
    setScoreData((s) => {
      const standard = { ...(s.metadata?.standard || {}), [name]: value };
      const current = { ...(s.metadata?.current || {}), [name]: value };
      return recomputeCompositeValue({ ...s, metadata: { standard, current } });
    });
  };

  const removeSubcategory = (name) => {
    setScoreData((s) => {
      const standard = { ...(s.metadata?.standard || {}) };
      const current = { ...(s.metadata?.current || {}) };
      delete standard[name];
      delete current[name];
      return recomputeCompositeValue({ ...s, metadata: { standard, current } });
    });
  };

  // La valeur totale d'un score composite est la somme de ses sous-catégories
  // (cohérent avec les scores existants : value === standard_value === somme(metadata.current) à l'init).
  const recomputeCompositeValue = (s) => {
    const total = Object.values(s.metadata?.standard || {}).reduce((sum, v) => sum + (Number(v) || 0), 0);
    return { ...s, value: total, standard_value: total };
  };

  const saveScore = () => {
    if (!scoreKey.trim()) return alert("L'identifiant du score est requis.");
    const cleaned = { ...scoreData };
    // Les champs optionnels arrivent de l'<input> en chaîne ("5"). Le serveur
    // les compare à des nombres (min(value + impact, max)) : une chaîne y lève
    // un TypeError, donc une erreur 500 à la première mesure validée.
    ["max", "coef", "standard_value"].forEach((f) => {
      if (cleaned[f] === "" || cleaned[f] === undefined || cleaned[f] === null) delete cleaned[f];
      else cleaned[f] = Number(cleaned[f]);
    });
    ["description", "detail", "unit"].forEach((f) => {
      if (cleaned[f] === "") delete cleaned[f];
    });
    if (cleaned.min === "") cleaned.min = 0;
    if (!cleaned.metadata) delete cleaned.metadata;

    const newDic = { ...config.score_dic };
    if (editingScoreKey && editingScoreKey !== scoreKey) delete newDic[editingScoreKey];
    newDic[scoreKey] = cleaned;
    set("score_dic", newDic);
    setShowScoreForm(false);
  };

  // Reprend tel quel le score d'un autre atelier — identifiant compris, c'est
  // lui que visent les mesures. Le sélecteur reste ouvert : on reprend
  // rarement UN acteur, plutôt les huit d'un atelier voisin.
  const importScore = (key, score) => {
    if (key in config.score_dic) return;
    set("score_dic", { ...config.score_dic, [key]: copyScore(score) });
  };

  const openScorePicker = (type) => {
    setScorePickerSearch("");
    setScorePickerType(type);
  };

  const scorePickerEntries = useMemo(
    () => (scorePickerType
      ? scoreCatalog(allConfigs, isNew ? null : configId, scorePickerType)
        .filter((e) => matchesScoreSearch(e, scorePickerSearch))
      : []),
    [allConfigs, configId, isNew, scorePickerType, scorePickerSearch],
  );

  const deleteScore = (key) => {
    const newDic = { ...config.score_dic };
    delete newDic[key];
    set("score_dic", newDic);
    if (showScoreForm && editingScoreKey === key) setShowScoreForm(false);
  };

  const handleSave = async () => {
    if (!config._id.trim()) return alert("L'identifiant de l'atelier est requis.");
    if (!config.name.trim()) return alert("Le nom de l'atelier est requis.");
    if (isNew) {
      if (!isValidSlug(config._id)) return alert(`Identifiant invalide.\n${SLUG_HINT}`);
      if (allConfigIds.includes(config._id)) return alert(`L'identifiant "${config._id}" est déjà utilisé par un autre atelier.`);
    }
    if (config.decision_metric_id && !(config.decision_metric_id in config.score_dic)) {
      return alert(`La métrique budget "${config.decision_metric_id}" ne correspond à aucun score défini dans cet atelier.`);
    }
    const payload = { ...config };
    // `last_page` a disparu : la conclusion est la dernière page de la pile
    // (cf. front/src/components/gameFlow.js). Un atelier qui traîne encore le
    // champ le perd au premier enregistrement — la migration a déjà remis la
    // page concernée en fin de pile, le garder ne ferait que semer le doute
    // sur ce qui fait autorité.
    delete payload.last_page;
    if (payload.nb_rounds_cg === "") delete payload.nb_rounds_cg;
    // Vide, zéro ou négatif : on n'enregistre rien, le défaut (20) s'applique.
    // Un 0 enregistré serait un réglage qui a l'air d'exister et ne fait rien.
    if (!(Number(payload.target_per_round) > 0)) delete payload.target_per_round;
    if (payload.custom_init === "") delete payload.custom_init;
    // Les couches du territoire sont RECALCULÉES à l'enregistrement, jamais
    // saisies (cf. territoryConfig.js). Le faire ici plutôt que dans un effet
    // évite de marquer l'atelier « modifié » à la simple ouverture, tout en
    // garantissant que ce qui part en base correspond à la pile telle qu'elle
    // est au moment où on l'enregistre.
    if (payload.territory?.id) {
      payload.territory = { ...payload.territory, layers: territoryLayers };
    } else {
      delete payload.territory;
    }
    await doSaveConfig(payload);
  };

  const doSaveConfig = async (payload) => {
    setSaving(true);
    const res = await apiPost("/save_game_config", payload);
    setSaving(false);
    if (res.success) { setDirty(false); alert("Atelier sauvegardé !"); navigate("/builder"); return; }
    if (res.blocked) {
      const errors = (res.findings || []).filter((f) => f.severity === "error");
      const details = errors.map((f) => `- ${f.message}`).join("\n");
      const forceOk = window.confirm(
        `Cet atelier contient ${errors.length} problème(s) qui provoqueront un plantage pour un joueur :\n\n${details}\n\nForcer l'enregistrement quand même ?`
      );
      if (forceOk) doSaveConfig({ ...payload, _force_save: true });
      return;
    }
    alert("Erreur : " + res.message);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer l'atelier "${config.name}" ? Cette action est irréversible.`)) return;
    const res = await apiDelete(`/delete_game_config/${config._id}`);
    if (res.success) navigate("/builder");
    else alert("Erreur : " + res.message);
  };

  const handleValidate = async () => {
    setValidating(true);
    const res = await apiGet(`/validate_game_config/${config._id}`);
    setValidating(false);
    if (res.error) { alert("Erreur : " + res.error); return; }
    setValidation(res.findings || []);
  };

  const filteredPages = allPages.filter((p) =>
    p._id.toLowerCase().includes(pageSearch.toLowerCase()) ||
    (p.title || "").toLowerCase().includes(pageSearch.toLowerCase()) ||
    (p.type || "").toLowerCase().includes(pageSearch.toLowerCase())
  );

  if (loading) return <div className="builder-loading">Chargement…</div>;

  const totalScores = Object.keys(config.score_dic).length;

  return (
    <div className="builder-container">
      <div className="builder-header">
        <button className="builder-back-btn" onClick={handleBack}>← Retour</button>
        <h1 className="builder-title">{isNew ? "Nouvel atelier" : `Éditer : ${config.name}`}</h1>
        <div className="builder-header-actions">
          {!isNew && (
            <button
              className="builder-btn-secondary"
              onClick={() => navigate(`/builder/config/${config._id}/graph`, { state: { config } })}
              title="Visualiser la succession des pages, décisions et conséquences"
            >
              🗺 Voir le graphe
            </button>
          )}
          {!isNew && (
            <button
              className="builder-btn-secondary"
              onClick={handleValidate}
              disabled={validating || dirty}
              title={dirty ? "Sauvegardez d'abord vos modifications : la vérification porte sur la version enregistrée en base." : "Vérifie les références (pages, décisions, scores) de cet atelier"}
            >
              {validating ? "Vérification…" : "🔍 Vérifier cet atelier"}
            </button>
          )}
          {!isNew && (
            <button
              className="builder-btn-secondary"
              onClick={() => setShowNotebooks((open) => !open)}
              disabled={dirty}
              title={dirty ? "Sauvegardez d'abord vos modifications : les carnets sont composés à partir de la version enregistrée en base." : "Générer les carnets de rôle imprimables de cet atelier"}
            >
              📕 Carnets de rôle
            </button>
          )}
          {!isNew && <button className="builder-btn-secondary" onClick={handleDuplicate}>Dupliquer</button>}
          {!isNew && <button className="builder-btn-danger" onClick={handleDelete}>Supprimer</button>}
          <button className="builder-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Sauvegarde…" : "Sauvegarder"}
          </button>
        </div>
      </div>

      {validation !== null && (
        <ValidationPanel findings={validation} onClose={() => setValidation(null)} />
      )}

      {showNotebooks && !isNew && (
        <NotebookPanel configId={config._id} token={token}
                       onClose={() => setShowNotebooks(false)} />
      )}

      {isNew && location.state?.duplicateFrom && (
        <div className="builder-hint" style={{ marginBottom: 16, padding: "8px 14px", background: "#eff6ff", borderRadius: 8, color: "#1e3a5f" }}>
          Copie de « {location.state.duplicateFrom.name} » — modifiez l'identifiant et le nom avant de sauvegarder.
        </div>
      )}

      {/* ── Section 1 : Infos générales ── */}
      <section className="builder-section">
        <h2 className="builder-section-title">Informations générales</h2>
        <div className="builder-form-grid">

          <label className="builder-label">
            <span className="field-label-row">
              Identifiant (slug) <InfoTooltip text={FIELD_TOOLTIPS._id} />
            </span>
            <input
              className="builder-input"
              value={config._id}
              onChange={(e) => set("_id", e.target.value)}
              disabled={!isNew}
              placeholder="ex: atelier_btp_v2"
            />
          </label>

          <label className="builder-label">
            <span className="field-label-row">
              Nom affiché <InfoTooltip text={FIELD_TOOLTIPS.name} />
            </span>
            <input
              className="builder-input"
              value={config.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="ex: Atelier BTP – Adaptation climatique"
            />
          </label>

          <label className="builder-label">
            <span className="field-label-row">
              Métrique budget <InfoTooltip text={FIELD_TOOLTIPS.decision_metric_id} />
            </span>
            <RefSelect
              value={config.decision_metric_id}
              onChange={(v) => set("decision_metric_id", v)}
              options={Object.entries(config.score_dic).map(([key, s]) => ({ value: key, label: s.name, sublabel: s.type }))}
              placeholder="Choisir le score utilisé comme budget…"
            />
          </label>

          <label className="builder-label">
            <span className="field-label-row">
              Mode d'init <InfoTooltip text={FIELD_TOOLTIPS.custom_init} />
            </span>
            <select
              className="builder-input"
              value={config.custom_init}
              onChange={(e) => set("custom_init", e.target.value)}
            >
              <option value="">— Aucun (mode standard) —</option>
              <option value="compte-gouttes">compte-gouttes (mode vote ACG)</option>
            </select>
          </label>

          <label className="builder-label">
            <span className="field-label-row">
              Nb rounds CG <InfoTooltip text={FIELD_TOOLTIPS.nb_rounds_cg} />
            </span>
            <input
              className="builder-input"
              type="number"
              value={config.nb_rounds_cg}
              onChange={(e) => set("nb_rounds_cg", e.target.value ? Number(e.target.value) : "")}
              placeholder="ex: 3"
            />
          </label>

          <label className="builder-label">
            <span className="field-label-row">
              Objectif eau par tour <InfoTooltip text={FIELD_TOOLTIPS.target_per_round} />
            </span>
            <input
              className="builder-input"
              type="number"
              min="1"
              value={config.target_per_round}
              onChange={(e) => set("target_per_round", e.target.value ? Number(e.target.value) : "")}
              placeholder="20"
            />
          </label>
        </div>

        <div style={{ marginTop: 16 }}>
          <div className="field-label-row" style={{ marginBottom: 8 }}>
            <span className="builder-label-text">Tags</span>
            <InfoTooltip text={FIELD_TOOLTIPS.tags} />
          </div>
          <TagInput
            tags={config.tags}
            onChange={(tags) => set("tags", tags)}
            suggestions={TAG_SUGGESTIONS}
          />
        </div>
      </section>

      {/* ── Section 1 bis : Territoire ── */}
      <section className="builder-section">
        <h2 className="builder-section-title">
          <span className="field-label-row">
            Territoire
            <InfoTooltip text="Carte 3D chargée pour toute la séance : elle sert au module d'intro, puis de fond aux pages qui le demandent. Sans territoire, l'atelier ne télécharge aucune donnée cartographique." />
          </span>
        </h2>

        {/* LE TERRITOIRE SE CHOISIT UNE FOIS. Les cadrages et les repères des
            pages de l'atelier sont exprimés dans les coordonnées de ce terrain :
            en changer ne les déplacerait pas, ça les rendrait absurdes sans rien
            signaler. Le champ est donc figé dès qu'il est posé — et le refus est
            aussi côté serveur, un verrou d'interface ne protégeant ni d'un appel
            direct à l'API ni du serveur MCP. */}
        <label className="builder-label" style={{ maxWidth: 420 }}>
          <span className="builder-label-text">Carte de l'atelier</span>
          <select
            className="builder-input"
            value={territoryId}
            disabled={territoryLocked}
            onChange={(e) => set("territory", e.target.value ? { id: e.target.value, layers: [] } : null)}
          >
            <option value="">— Aucun (atelier sans carte) —</option>
            {territories.map((t) => (
              <option key={t.id} value={t.id}>{t.label || t.id}</option>
            ))}
          </select>
          {territoryLocked && (
            <span className="builder-hint">
              Le territoire d'un atelier ne change pas. Pour en monter une version
              ailleurs, utilisez <strong>Dupliquer</strong>.
            </span>
          )}
        </label>

        {territoryId && (
          <div className="builder-territory-summary">
            <p className="builder-hint">
              Les couches sont <strong>déduites de la pile</strong> à l'enregistrement, pas saisies :
              une couche qu'une page allume sans qu'elle ait été chargée n'affiche rien et ne
              signale rien. Elles sont construites une fois pour la séance — en ajouter une en
              cours de partie reconstruirait la carte.
            </p>

            <div className="builder-territory-layers">
              {territoryLayers.length === 0 && (
                <span className="builder-hint">
                  Aucune page de la pile n'utilise la carte pour l'instant.
                </span>
              )}
              {territoryLayers.map((layer) => (
                <span
                  key={layer}
                  className={`builder-chip${unknownLayers.includes(layer) ? " is-warning" : ""}`}
                  title={unknownLayers.includes(layer)
                    ? `« ${layer} » n'existe pas dans le datapack de ce territoire : rien ne s'affichera.`
                    : undefined}
                >
                  {layer}{unknownLayers.includes(layer) && " ⚠"}
                </span>
              ))}
            </div>

            {territoryPages.length > 0 && (
              <table className="builder-territory-table">
                <tbody>
                  {territoryPages.map((user) => (
                    <tr key={user.pageId}>
                      <td>
                        <button
                          type="button"
                          className="builder-linkbtn"
                          onClick={() => openPageComposer(user.pageId)}
                        >
                          {user.title}
                        </button>
                      </td>
                      <td className="builder-territory-usage">{user.usage}</td>
                      <td className="builder-territory-usage">{user.layers.join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>

      {/* ── Section 2 : Pile de pages ── */}
      <section className="builder-section">
        <div className="builder-section-header">
          <h2 className="builder-section-title" style={{ margin: 0 }}>
            <span className="field-label-row">
              Pile de pages ({config.pile.length})
              <InfoTooltip text={FIELD_TOOLTIPS.pile} />
            </span>
          </h2>
          <button className="builder-btn-secondary" onClick={() => openPagePickerAt(null)}>
            + Ajouter une page
          </button>
        </div>

        {showPagePicker && (
          <div className="builder-picker-overlay" onClick={() => setShowPagePicker(false)}>
            <div className="builder-picker" onClick={(e) => e.stopPropagation()}>
              <div className="builder-picker-header">
                <h3>Ajouter une page</h3>
                <button onClick={() => setShowPagePicker(false)}>✕</button>
              </div>

              {/* DEUX CHEMINS, PAS UN CHEMIN ET UNE NOTE DE BAS DE PAGE.
                  Écrire une page neuve est au moins aussi fréquent que reprendre
                  une page existante ; le geste était pourtant relégué dans le
                  pied du sélecteur, sous une liste de soixante lignes qu'il
                  fallait dépasser pour le voir. Il ouvre maintenant l'écran. */}
              <button
                type="button"
                className="builder-picker-create"
                onClick={() => { setShowPagePicker(false); setShowPageTypePicker(true); }}
              >
                <span className="builder-picker-create-plus">＋</span>
                <span>
                  <strong>Créer une nouvelle page</strong>
                  <span className="builder-picker-create-hint">
                    Choisir un type, puis composer — elle entrera dans la pile
                    {insertAt === null ? " à la fin" : ` en position ${insertAt + 1}`}.
                  </span>
                </span>
              </button>

              <div className="builder-picker-or"><span>ou reprendre une page existante</span></div>

              <input
                className="builder-search"
                placeholder="Rechercher par ID, titre ou type…"
                value={pageSearch}
                onChange={(e) => setPageSearch(e.target.value)}
                autoFocus
              />
              <div className="builder-picker-list">
                {filteredPages.slice(0, 60).map((p) => {
                  // MONTRÉE MAIS REFUSÉE, plutôt que masquée : une page qu'on
                  // cherche et qui n'apparaît pas laisse croire à un problème de
                  // recherche. Ici on la voit, et on lit pourquoi elle ne peut
                  // pas entrer.
                  const fit = pageFitsConfig(p, territoryId);
                  return (
                    <div
                      key={p._id}
                      className={`builder-picker-item${fit.ok ? "" : " is-blocked"}`}
                      title={fit.ok ? undefined : fit.reason}
                      onClick={() => fit.ok && addPageToPile(p._id)}
                    >
                      <span className="builder-picker-id">{p._id}</span>
                      <span className="builder-picker-title">{p.title || "—"}</span>
                      <span className="builder-tag tag-type">{p.type}</span>
                      {!fit.ok && <span className="builder-picker-blocked">{fit.reason}</span>}
                    </div>
                  );
                })}
                {filteredPages.length === 0 && <div className="builder-empty">Aucune page trouvée</div>}
              </div>
            </div>
          </div>
        )}

        {showPageTypePicker && (
          <PageTypePicker
            onPick={createPageInPile}
            onCancel={() => setShowPageTypePicker(false)}
            onBack={() => { setShowPageTypePicker(false); setShowPagePicker(true); }}
            blocked={territoryId ? {} : {
              territory_intro:
                "Cet atelier n'a pas de carte : choisissez un territoire dans la section Territoire, au-dessus.",
            }}
          />
        )}

        <div className="builder-pile" style={{ marginTop: 12 }}>
          {config.pile.length === 0 && (
            <div className="builder-empty">La pile est vide. Ajoutez des pages ci-dessus.</div>
          )}
          <SortableList
            items={pileItems}
            onReorder={reorderPile}
            renderItem={(item, idx) => {
              const page = allPages.find((p) => p._id === item.pageId);
              return (
                <div className="builder-pile-item">
                  <span className="builder-pile-index">{idx + 1}</span>
                  <div className="builder-pile-info">
                    <span className="builder-pile-id">{item.pageId}</span>
                    {page && (
                      <span className="builder-pile-title">
                        {page.title || ""}
                        <span className="builder-tag tag-type" style={{ marginLeft: 6 }}>{page.type}</span>
                      </span>
                    )}
                    {!page && <span className="builder-pile-missing">⚠ Page introuvable en base</span>}
                  </div>
                  <div className="builder-pile-actions">
                    {/* UN SEUL BOUTON là où il y en avait deux (👁 aperçu, ✏ édition) :
                        voir la page et la modifier se font désormais au même endroit. */}
                    {page && (
                      <button
                        className="builder-icon-btn"
                        onClick={() => openPageComposer(item.pageId)}
                        title="Ouvrir la page — aperçu et édition"
                      >👁</button>
                    )}
                    <button className="builder-icon-btn" onClick={() => movePage(idx, -1)} disabled={idx === 0} title="Monter">↑</button>
                    <button className="builder-icon-btn" onClick={() => movePage(idx, 1)} disabled={idx === config.pile.length - 1} title="Descendre">↓</button>
                    <button className="builder-icon-btn danger" onClick={() => removePage(idx)} title="Retirer de la pile">✕</button>
                    {page && (
                      <button
                        className="builder-icon-btn"
                        onClick={() => handleDuplicatePageInPile(idx, item.pageId)}
                        title="Dupliquer cette page pour cet atelier uniquement (les autres ateliers gardent l'originale)"
                      >⧉</button>
                    )}
                  </div>
                </div>
              );
            }}
          />
        </div>
      </section>

      {/* ── Section 3 : Réservoir de mesures ──
          Après la pile, parce qu'il ne se dimensionne qu'en regard d'elle :
          ce sont les pages qui piochent qui disent combien de mesures il faut. */}
      <MeasurePoolSection
        config={config}
        onSet={set}
        pagesById={pagesById}
        token={token}
      />

      {/* ── Section 4 : Dictionnaire de scores ── */}
      <section className="builder-section">
        <div className="builder-section-header">
          <h2 className="builder-section-title" style={{ margin: 0 }}>
            <span className="field-label-row">
              Scores ({totalScores})
              <InfoTooltip text={FIELD_TOOLTIPS.score_dic} />
            </span>
          </h2>
        </div>

        {/* GROUPÉS PAR NATURE, ET TOUS LES GROUPES SONT RENDUS — même vides.
            Le bouton de création vit dans l'en-tête de son groupe : « ajouter
            un acteur » se lit à côté des acteurs, et non dans une rangée de
            boutons en tête de section où il fallait deviner à quoi correspond
            « + cbs-submetric ». Corollaire : un groupe vide doit rester
            visible, sinon on ne peut plus créer le premier score de sa nature. */}
        {SCORE_TYPES.map((type) => {
          const entries = scoresByType[type];
          return (
            <div key={type} className="score-group">
              <div className="score-group-header">
                <span className={`builder-tag tag-${type}`}>{SCORE_TYPE_LABELS[type]}</span>
                <span className="score-group-desc">{SCORE_TYPE_DESC[type]}</span>
                <span className="score-group-count">{entries.length}</span>
                <button
                  className="builder-btn-secondary small"
                  onClick={() => openScorePicker(type)}
                  title={`Créer un score de type « ${SCORE_TYPE_LABELS[type]} », ou en reprendre un d'un autre atelier`}
                >+ Ajouter</button>
              </div>
              {entries.length === 0 ? (
                <div className="score-group-empty">Aucun score de ce type.</div>
              ) : (
                <div className="builder-scores-grid">
                  {entries.map(([key, score]) => (
                    <ScoreCard
                      key={key}
                      scoreKey={key}
                      score={score}
                      onEdit={() => openEditScore(key)}
                      onDelete={() => deleteScore(key)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* LE COMPOSEUR S'OUVRE PAR-DESSUS L'ATELIER, il ne le remplace pas :
            l'atelier a son propre brouillon (pile, scores), et naviguer vers
            une autre route le perdrait. */}
        {composerPageId && (
          <PageComposer
            pageId={composerPageId}
            newType={composerNewType}
            config={config}
            onClose={closePageComposer}
            onSaved={(savedId) => {
              apiGet("/get_all_pages").then(setAllPages);
              // La page créée depuis la pile y prend la place qu'on avait visée.
              // Une seule fois : les enregistrements suivants ne la dupliquent pas.
              if (creatingAt !== null && savedId && !config.pile.includes(savedId)) {
                const pile = [...config.pile];
                pile.splice(Math.min(creatingAt, pile.length), 0, savedId);
                set("pile", pile);
              }
              setCreatingAt(null);
            }}
            onNavigate={(nextId) => { setComposerNewType(null); setCreatingAt(null); setComposerPageId(nextId); }}
            onRemoveFromPile={(pageId) => {
              set("pile", config.pile.filter((id) => id !== pageId));
              setComposerPageId(null);
            }}
          />
        )}

        {/* « + Ajouter » un score : DEUX CHEMINS, comme « Ajouter une page ».
            Sans le second, un score déjà défini ailleurs devait être retapé —
            identifiant compris, et une coquille dans l'identifiant le coupe de
            toutes les mesures qui le visent, sans erreur. */}
        {scorePickerType && (
          <div className="builder-picker-overlay" onClick={() => setScorePickerType(null)}>
            <div className="builder-picker" onClick={(e) => e.stopPropagation()}>
              <div className="builder-picker-header">
                <h3>Ajouter un score · {SCORE_TYPE_LABELS[scorePickerType]}</h3>
                <button onClick={() => setScorePickerType(null)}>✕</button>
              </div>

              <button
                type="button"
                className="builder-picker-create"
                onClick={() => { const t = scorePickerType; setScorePickerType(null); openNewScore(t); }}
              >
                <span className="builder-picker-create-plus">＋</span>
                <span>
                  <strong>Créer un nouveau score</strong>
                  <span className="builder-picker-create-hint">Formulaire vierge : identifiant, nom, valeur de départ, bornes.</span>
                </span>
              </button>

              <div className="builder-picker-or"><span>ou reprendre un score d'un autre atelier</span></div>

              <input
                className="builder-search"
                placeholder="Rechercher par ID, nom ou atelier…"
                value={scorePickerSearch}
                onChange={(e) => setScorePickerSearch(e.target.value)}
                autoFocus
              />
              <div className="builder-picker-list">
                {scorePickerEntries.map((entry) => {
                  // DÉJÀ LÀ = MONTRÉ MAIS REFUSÉ, comme une page qui n'entre pas :
                  // c'est aussi ce qui confirme qu'une reprise a pris.
                  const present = entry.key in config.score_dic;
                  const several = entry.variants.length > 1;
                  return (
                    <div key={entry.key} className="score-pick">
                      <div
                        className={`builder-picker-item${present ? " score-pick-present" : several ? " score-pick-head" : ""}`}
                        onClick={() => !present && !several && importScore(entry.key, entry.variants[0].score)}
                      >
                        {entry.variants[0].score.icon && (
                          <img src={entry.variants[0].score.icon} alt="" className="builder-score-icon" />
                        )}
                        <span className="builder-picker-id">{entry.key}</span>
                        <span className="builder-picker-title">{entry.variants[0].score.name || "—"}</span>
                        <span className="builder-tag tag-type">
                          {entry.configCount} atelier{entry.configCount > 1 ? "s" : ""}
                        </span>
                        {present && <span className="score-pick-note">✓ Déjà dans cet atelier</span>}
                        {!present && !several && (
                          <span className="score-pick-values">{scoreSummary(entry.variants[0].score)}</span>
                        )}
                      </div>
                      {/* Plusieurs définitions sous le même identifiant : on ne
                          choisit pas à la place de l'auteur. */}
                      {!present && several && entry.variants.map((v, i) => (
                        <div
                          key={i}
                          className="builder-picker-item score-pick-variant"
                          onClick={() => importScore(entry.key, v.score)}
                          title={v.configs.join(", ")}
                        >
                          <span className="builder-picker-title">
                            {v.score.name || "—"} · <span className="score-pick-values">{scoreSummary(v.score)}</span>
                          </span>
                          <span className="score-pick-configs">
                            {v.configs.slice(0, 3).join(", ")}{v.configs.length > 3 ? ` +${v.configs.length - 3}` : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })}
                {scorePickerEntries.length === 0 && (
                  <div className="builder-empty">
                    {scorePickerSearch ? "Aucun score trouvé" : "Aucun autre atelier ne définit de score de ce type"}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Score form modal */}
        {showScoreForm && (
          <div className="builder-picker-overlay" onClick={() => setShowScoreForm(false)}>
            <div className="builder-picker wide" onClick={(e) => e.stopPropagation()}>
              <div className="builder-picker-header">
                <h3>{editingScoreKey ? `Éditer : ${editingScoreKey}` : `Nouveau score · ${scoreData.type}`}</h3>
                <button onClick={() => setShowScoreForm(false)}>✕</button>
              </div>

              <div className="builder-picker-body">
                {/* Type tabs */}
                <div className="score-type-tabs">
                  {SCORE_TYPES.map((t) => (
                    <button
                      key={t}
                      className={`score-type-tab ${scoreData.type === t ? "active" : ""}`}
                      onClick={() => setScoreData((s) => ({ ...s, type: t }))}
                      disabled={!!editingScoreKey}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <p className="builder-hint" style={{ marginTop: 4 }}>{SCORE_TYPE_DESC[scoreData.type]}</p>

                <div className="builder-form-grid" style={{ marginTop: 12 }}>
                  <label className="builder-label">
                    <span className="field-label-row">
                      Identifiant <InfoTooltip text="Clé unique du score dans score_dic. Doit correspondre aux IDs utilisés dans les décisions." />
                    </span>
                    <input
                      className="builder-input"
                      value={scoreKey}
                      onChange={(e) => setScoreKey(e.target.value)}
                      placeholder="ex: money, water, architecte"
                      disabled={!!editingScoreKey}
                    />
                  </label>

                  <label className="builder-label">
                    <span className="field-label-row">
                      Nom affiché <InfoTooltip text="Nom lisible affiché dans l'interface du jeu." />
                    </span>
                    <input
                      className="builder-input"
                      value={scoreData.name}
                      onChange={(e) => setScoreData((s) => ({ ...s, name: e.target.value }))}
                      placeholder="ex: Coût du projet"
                    />
                  </label>

                  <label className="builder-label">
                    <span className="field-label-row">
                      Valeur initiale <InfoTooltip text={scoreData.metadata ? "Calculée automatiquement : somme des sous-catégories ci-dessous." : "Valeur au début du jeu, avant toute décision."} />
                    </span>
                    <input className="builder-input" type="number" value={scoreData.value}
                      disabled={!!scoreData.metadata}
                      onChange={(e) => setScoreData((s) => ({ ...s, value: Number(e.target.value) }))} />
                  </label>

                  <label className="builder-label">
                    <span className="field-label-row">
                      Min <InfoTooltip text="Valeur plancher. Le score ne peut pas descendre en dessous." />
                    </span>
                    <input className="builder-input" type="number" value={scoreData.min}
                      onChange={(e) => setScoreData((s) => ({ ...s, min: Number(e.target.value) }))} />
                  </label>

                  <label className="builder-label">
                    <span className="field-label-row">
                      Max <InfoTooltip text="Valeur plafond (optionnel). Laisser vide si pas de maximum." />
                    </span>
                    <input className="builder-input" type="number" value={scoreData.max}
                      onChange={(e) => setScoreData((s) => ({ ...s, max: e.target.value }))}
                      placeholder="Pas de max" />
                  </label>

                  {/* L'ICÔNE S'ENVOIE, elle ne se recopie pas. Coller une URL
                      Cloudinary supposait d'aller la chercher dans une autre
                      application ; le glisser-déposer est le geste employé
                      partout ailleurs dans le builder (IconUpload). Le 🔗 du
                      composant garde la saisie manuelle pour les icônes déjà
                      hébergées. */}
                  <div className="builder-label score-icon-field" style={{ gridColumn: "1 / -1" }}>
                    <span className="field-label-row">
                      Icône <InfoTooltip text="Image affichée à côté du score dans le jeu. Glissez un fichier ici, ou cliquez pour le choisir — il part sur Cloudinary. Le bouton 🔗 permet de coller une URL existante." />
                    </span>
                    <IconUpload
                      value={scoreData.icon}
                      token={token}
                      onChange={(url) => setScoreData((s) => ({ ...s, icon: url }))}
                      title="Icône du score"
                    />
                  </div>

                  {scoreData.type === "cbs-submetric" && (
                    <>
                      <label className="builder-label">
                        <span className="field-label-row">
                          Coefficient CBS <InfoTooltip text="Coefficient de perméabilité entre 0.0 (imperméable) et 1.0 (végétation en pleine terre). Multiplie la surface pour le calcul du CBS." />
                        </span>
                        <input className="builder-input" type="number" step="0.1" min="0" max="1"
                          value={scoreData.coef}
                          onChange={(e) => setScoreData((s) => ({ ...s, coef: Number(e.target.value) }))}
                          placeholder="ex: 0.5" />
                      </label>
                      <label className="builder-label">
                        <span className="field-label-row">
                          Détail <InfoTooltip text="Description courte du type de surface (ex: 'Bitume, béton, toiture en tôle')." />
                        </span>
                        <input className="builder-input" value={scoreData.detail}
                          onChange={(e) => setScoreData((s) => ({ ...s, detail: e.target.value }))}
                          placeholder="ex: Dalles ajourées, graviers drainants" />
                      </label>
                    </>
                  )}

                  {scoreData.type === "actor" && (
                    <>
                      <label className="builder-label" style={{ gridColumn: "1 / -1" }}>
                        <span className="field-label-row">
                          Description du rôle <InfoTooltip text="Texte affiché aux joueurs décrivant la position et les intérêts de cet acteur." />
                        </span>
                        <textarea className="builder-textarea" rows={4} value={scoreData.description}
                          onChange={(e) => setScoreData((s) => ({ ...s, description: e.target.value }))}
                          placeholder="Décrivez le rôle et les priorités de cet acteur dans le jeu…" />
                      </label>
                    </>
                  )}

                  <label className="builder-label">
                    <span className="field-label-row">
                      Valeur standard <InfoTooltip text={scoreData.metadata ? "Calculée automatiquement : somme des sous-catégories ci-dessous." : "Valeur de référence pour comparaison (ex: empreinte eau standard avant décisions). Utilisée dans les graphes de comparaison."} />
                    </span>
                    <input className="builder-input" type="number" value={scoreData.standard_value}
                      disabled={!!scoreData.metadata}
                      onChange={(e) => setScoreData((s) => ({ ...s, standard_value: e.target.value }))}
                      placeholder="Optionnel" />
                  </label>

                  {scoreData.type === "metric" && (
                    <div style={{ gridColumn: "1 / -1", marginTop: 8 }}>
                      <label className="builder-label" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                        <input type="checkbox" checked={!!scoreData.metadata}
                          onChange={(e) => toggleComposite(e.target.checked)} />
                        <span className="field-label-row">
                          Score composite (avec sous-catégories)
                          <InfoTooltip text="Décompose ce score en sous-catégories (ex: empreinte eau par usage : WC, douches, arrosage…). Affiché sous forme de donut détaillé dans le jeu. La valeur totale est calculée automatiquement." />
                        </span>
                      </label>

                      {scoreData.metadata && (
                        <label className="builder-label" style={{ marginTop: 10, maxWidth: 220 }}>
                          <span className="field-label-row">
                            Unité <InfoTooltip text="Unité affichée par le graphe des pages commande & contrôle. Vide = m³." />
                          </span>
                          <input className="builder-input" value={scoreData.unit || ""}
                            onChange={(e) => setScoreData((s) => ({ ...s, unit: e.target.value }))}
                            placeholder="m³" />
                        </label>
                      )}

                      {scoreData.metadata && (
                        <div style={{ marginTop: 10, padding: 12, background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                          <div className="builder-hint" style={{ marginBottom: 8 }}>
                            Sous-catégories et leur valeur standard (le total ci-dessus est recalculé automatiquement).
                            Le détail « actuel » (metadata.current) démarre identique et évolue via les impacts détaillés des décisions.
                          </div>
                          {Object.entries(scoreData.metadata.standard || {}).map(([name, value]) => (
                            <div key={name} className="builder-inline-row">
                              <input className="builder-input" defaultValue={name}
                                onBlur={(e) => renameSubcategory(name, e.target.value.trim())}
                                placeholder="Nom de la sous-catégorie" />
                              <input className="builder-input small" type="number" value={value}
                                onChange={(e) => updateSubcategoryValue(name, Number(e.target.value))} />
                              <button className="builder-icon-btn danger" onClick={() => removeSubcategory(name)}>✕</button>
                            </div>
                          ))}
                          <button className="builder-btn-secondary small" onClick={addSubcategory} style={{ marginTop: 4 }}>
                            + Ajouter une sous-catégorie
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="builder-picker-footer">
                {editingScoreKey && (
                  <button className="builder-btn-danger" onClick={() => { deleteScore(editingScoreKey); setShowScoreForm(false); }}>
                    Supprimer ce score
                  </button>
                )}
                <button className="builder-btn-secondary" onClick={() => setShowScoreForm(false)}>Annuler</button>
                <button className="builder-btn-primary" onClick={saveScore}>Valider</button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

const VALIDATION_SEVERITY_ORDER = { error: 0, warning: 1, info: 2 };
const VALIDATION_SEVERITY_LABEL = { error: "Erreur", warning: "Attention", info: "Info" };

function ValidationPanel({ findings, onClose }) {
  const counts = { error: 0, warning: 0, info: 0 };
  findings.forEach((f) => { counts[f.severity] = (counts[f.severity] || 0) + 1; });
  const sorted = [...findings].sort(
    (a, b) => VALIDATION_SEVERITY_ORDER[a.severity] - VALIDATION_SEVERITY_ORDER[b.severity]
  );

  return (
    <div className="validation-panel">
      <div className="validation-summary">
        <span>Résultat de la vérification :</span>
        <span className="validation-count validation-count-error">{counts.error} erreur{counts.error !== 1 ? "s" : ""}</span>
        <span className="validation-count validation-count-warning">{counts.warning} avertissement{counts.warning !== 1 ? "s" : ""}</span>
        <span className="validation-count validation-count-info">{counts.info} info{counts.info !== 1 ? "s" : ""}</span>
        <button className="builder-icon-btn" onClick={onClose} title="Fermer" style={{ marginLeft: "auto" }}>✕</button>
      </div>
      {sorted.length === 0 ? (
        <div className="builder-empty">Aucun problème détecté — cet atelier est valide.</div>
      ) : (
        sorted.map((f, i) => (
          <div key={i} className={`validation-finding validation-finding-${f.severity}`}>
            [{VALIDATION_SEVERITY_LABEL[f.severity] || f.severity}] {f.message}
            {(f.page_id || f.decision_id || f.score_id) && (
              <span className="validation-finding-location">
                {f.page_id && `page: ${f.page_id}`}
                {f.decision_id && `  décision: ${f.decision_id}`}
                {f.score_id && `  score: ${f.score_id}`}
              </span>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// Ce qui distingue deux définitions d'un même score, en une ligne.
function scoreSummary(score) {
  const parts = [`val ${score.value}`];
  if (score.min !== undefined) parts.push(`min ${score.min}`);
  if (score.max !== undefined) parts.push(`max ${score.max}`);
  if (score.coef !== undefined) parts.push(`coef ${score.coef}`);
  if (score.metadata) parts.push("composite");
  return parts.join(" · ");
}

function ScoreCard({ scoreKey, score, onEdit, onDelete }) {
  return (
    <div className="builder-score-card" onClick={onEdit} style={{ cursor: "pointer" }}>
      <div className="builder-score-card-header">
        {score.icon && (
          <img src={score.icon} alt="" className="builder-score-icon" />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="builder-score-name">
            {score.name || scoreKey}
            {score.metadata && <span className="builder-tag tag-type" style={{ marginLeft: 6 }}>composite</span>}
          </div>
          <div className="builder-score-key">{scoreKey}</div>
        </div>
      </div>
      <div className="builder-score-values">
        <span>val: <b>{score.value}</b></span>
        {score.min !== undefined && <span> min: <b>{score.min}</b></span>}
        {score.max !== undefined && <span> max: <b>{score.max}</b></span>}
        {score.coef !== undefined && <span> coef: <b>{score.coef}</b></span>}
      </div>
      <div className="builder-score-card-actions" onClick={(e) => e.stopPropagation()}>
        <button className="builder-btn-secondary small" onClick={onEdit}>Éditer</button>
        <button className="builder-btn-danger small" onClick={onDelete}>Supprimer</button>
      </div>
    </div>
  );
}
